import { Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Pergunta em quantas colunas as imagens recém-selecionadas devem entrar.
 *
 * O menu tem UMA opção de imagens: quantas o usuário escolhe no seletor é que decide
 * o formato do campo. Uma imagem vira um card comum (sem pergunta); várias passam por
 * aqui, e a resposta define se saem como cards separados (1 coluna) ou como um único
 * grupo em 2/3 colunas.
 *
 * A pergunta vive num serviço porque quem precisa dela é o fluxo de upload
 * (`EditItemsService`), no meio de uma cadeia RxJS — o modal é renderizado uma vez
 * por `section-items` e responde por este canal.
 */
@Injectable({ providedIn: 'root' })
export class ImageColumnsService {
    /** Quantas imagens estão esperando a resposta (null = nenhuma pergunta aberta). */
    private _pendingImagesCount = signal<number | null>(null);
    public pendingImagesCount = this._pendingImagesCount.asReadonly();

    private _answer?: Subject<number>;

    /**
     * Abre a pergunta e emite a escolha (1, 2 ou 3).
     *
     * Cancelar completa sem emitir: quem estiver no `switchMap` encerra o fluxo sem
     * subir nada, que é o que o usuário espera de um cancelamento.
     */
    public ask(imagesCount: number): Observable<number> {
        // Uma pergunta por vez: se sobrou alguma pendente (fluxo abortado por erro),
        // ela é encerrada aqui para não deixar o assinante preso.
        this._answer?.complete();

        const answer = new Subject<number>();
        this._answer = answer;
        this._pendingImagesCount.set(imagesCount);

        return answer.asObservable();
    }

    /** Responde a pergunta aberta com o número de colunas escolhido. */
    public choose(columns: number): void {
        const answer = this._answer;
        this._close();
        answer?.next(columns);
        answer?.complete();
    }

    /** Fecha sem escolher — o lote selecionado é descartado. */
    public cancel(): void {
        const answer = this._answer;
        this._close();
        answer?.complete();
    }

    private _close(): void {
        this._answer = undefined;
        this._pendingImagesCount.set(null);
    }
}
